import React from 'react'
import { twJoin, twMerge } from 'tailwind-merge'

interface RadioGroupProps<T extends string> {
  options: Option<T>[]
  selected: T
  className?: string
  disabled?: boolean
  onChange: (value: T) => void
  error?: string
  label?: string
  required?: boolean
  inline?: boolean
  containerWrapperClassName?: string
  optionsWrapperClassName?: string
}

// export type OptionValue = string | number | null | boolean

export interface Option<T extends string> {
  value: T
  description?: string
  label: string
}

const RadioGroup = <T extends string>({
  options,
  selected,
  onChange,
  disabled,
  error,
  label,
  required,
  inline = false,
  containerWrapperClassName,
  optionsWrapperClassName,
}: RadioGroupProps<T>) => {
  return (
    <div className={`flex flex-col gap-1 ${containerWrapperClassName || ''}`}>
      {(label || required) && (
        <div className="flex gap-0.5 text-sm font-medium text-text-primary">
          {label && <span>{label}</span>}
          {required && <span className="text-danger">*</span>}
        </div>
      )}
      <ul className={`${inline ? 'flex gap-2' : ''}`}>
        {options.map(option => (
          <li
            key={option.label}
            className={`${inline ? '' : 'mb-4'} ${optionsWrapperClassName || ''}`}
          >
            <label
              className={`relative flex items-start gap-2 text-sm ${
                !disabled && 'cursor-pointer'
              } w-fit max-w-fit`}
            >
              <div>
                <input
                  className={twMerge(
                    'size-[20px] rounded-full border-2 outline-none ring-brand-primary ring-offset-1 transition-colors duration-200 ease-linear focus-visible:ring-2',
                    disabled ? 'border-fill-primary/70' : 'border-fill-primary',
                    option.value === selected ? 'bg-background-secondary' : 'bg-transparent',
                  )}
                  disabled={disabled}
                  checked={option.value === selected}
                  value={`${option.value}`}
                  onChange={e => {
                    onChange(e.target.value as T)
                  }}
                  type="radio"
                />
              </div>
              <div>
                <span
                  className={twJoin(
                    'text-sm text-text-primary transition-colors duration-200 ease-linear',
                    disabled && 'opacity-70',
                  )}
                >
                  {option.label}
                </span>
                {option.description && <p className="text-sm">{option.description}</p>}
              </div>
            </label>
          </li>
        ))}
      </ul>
      {error && <p className="mt-2 text-sm text-red">{error}</p>}
    </div>
  )
}

export default RadioGroup
